import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type SxProps, type Theme } from '@mui/material';

import DropdownSelect, { DropdownSelectOption, DropdownSelectProps } from '@/components/fields/DropdownSelect';
import { IndicatorChipVariant } from '@/components/statuses/IndicatorChip';

interface StatusDropdownSelectProps {
  activeOptions?: string[];
  name: string;
  value: string;
  onChange: (newVal: string) => void;
  sx?: SxProps<Theme>;
}

const createStatusDropdownSelect = ({
  options,
  defaultSx,
  translationNamespace
}: {
  options: IndicatorChipVariant[];
  defaultSx: SxProps<Theme>;
  translationNamespace?: string;
}) => {
  const StatusDropdownSelect: FC<StatusDropdownSelectProps & Omit<DropdownSelectProps, 'onChange'>> = ({
    activeOptions,
    value,
    name,
    onChange,
    ...props
  }) => {
    const { t } = useTranslation(translationNamespace);

    const activeStatuses = useMemo(() => {
      if (!activeOptions) return options;

      return activeOptions
        .map(optionValue => options.find(({ value }) => value === optionValue))
        .filter(Boolean) as IndicatorChipVariant[];
    }, [activeOptions]);

    const activeTranslatedStatuses = useMemo(
      () =>
        activeStatuses.map(status => {
          const { label, Icon } = status;

          return {
            ...status,
            Icon: Icon?.(),
            label: typeof label === 'function' ? label(t) : label
          };
        }) as DropdownSelectOption[],
      [activeStatuses, t]
    );

    const handleChange = useCallback((value: string | string[]) => onChange(value as string), [onChange]);

    return (
      <DropdownSelect
        options={activeTranslatedStatuses}
        value={value}
        onChange={handleChange}
        name={name}
        isMulti={false}
        isSearchable={false}
        sx={defaultSx}
        {...props}
      />
    );
  };

  return StatusDropdownSelect;
};

export default createStatusDropdownSelect;
